// Copyright: IQGeo Limited 2010-2019
import _ from 'underscore';
import config from 'myWorld/base/config';
import myw from 'myWorld/base/core';
import L from 'leaflet';
import 'myWorld/base/util';
import 'myWorld/base/leafletExtensions';
import 'myWorld/base/svgRenderer';
import './circle';
import './polygonMarker';
import './textMarker';
import 'leaflet-geometryutil';

myw.FeatureRepresentation = myw.MywClass.extend('FeatureRepresentation', {
    /**
     * Initializes a Map representation of FEATURE, creating a leaflet overlay
     * @class  Map representation of a feature.
     * Has the knowledge to create the correct type of leaflet ILayer to be able to add it to a map.
     * Provides highlight behaviour
     * @param  {Feature}            feature       Feature of which to create a representation of
     * @param  {object}                  options
     * @constructs
     */

    initialize(feature, options) {
        this.options = options = options || {};

        /** The feature being represented.
         * @type {Feature} */
        this.feature = feature;
        const getGeom = this.feature.getGeometryFieldNameInWorld;

        /** The name of the world this map will be displaying.
         * @type {string} */
        this.worldName = options.worldName || 'geo';

        /** The name of the geometery field. If undefined then geometryFieldName is set to primary geometry
         * @type {string} */
        this.geometryFieldName =
            options.geomFieldName ||
            (getGeom && this.feature.getGeometryFieldNameInWorld(this.worldName));

        /** The map on which the feature is being displayed.
         * @type {MapControl} */
        this.map = null;

        /**  Styles to use when rendering the feature. If null, default styles from the configuration will be used
         * @type {styleDefinition}  */
        this.styles = options.styles ? options.styles : this._getDefaultStyles(); // store the styles for the feature.

        // Dimensions of arrow in line weight units.
        this.arrowHeight = 5;
        this.arrowWidth = 4;

        // Sets the leafet object that is added to the map. @type {L.ILayer}
        this._setOverlay();

        /** Event handlers for the created leaflet overlay
            @type {Array<eventHandler>}  */
        this._setupEventHandlers(options.eventHandlers);

        // Bounds of this feature.
        this._bounds = null;
    },

    /**
     * Highlights self
     * Changes the style of self to the highlight style
     */
    highlight() {
        this.setStyle(this.styles.highlight);
        if (this.map) this._overlay.bringToFront(); //Highlighted style will persist when map becomes available
        this.highlighted = true;
    },

    /**
     * Changes the style of self to the normal style
     */
    unHighlight() {
        if (this.styles) this.setStyle(this.styles.normal);
        this.highlighted = false;
    },

    /**
     * Adds self to a map
     * @param {MapControl} map The map on which to visualize self
     */
    addToMap(map) {
        this.map = map;
        map.addLayer(this._overlay);
        if (this._shouldSendToBack()) {
            this._overlay.bringToBack();
        }
    },

    /**
     * Remove self from its current map
     */
    removeFromMap() {
        const map = this.map;
        this.map = null;
        if (map) map.removeLayer(this._overlay);
    },

    /**
     * Returns the geometry being represented by self
     */
    getGeometry() {
        if (this.geometryFieldName) {
            return this.feature.getGeometry(this.geometryFieldName);
        } else {
            return this.feature.getGeometryInWorld(this.worldName);
        }
    },

    /**
     * Refreshes the representation with the details from an updated feature
     * @param  {Feature} feature Updated feature
     */
    update(feature) {
        const prevOverlay = this._overlay;
        if (
            !feature.styleDependsOnSession && 
            prevOverlay &&
            feature &&
            _.isEqual(feature.properties, this.feature.properties) &&
            _.isEqual(feature.geometry, this.feature.geometry)
        )
            return; //no changes that require rebuilding the overlay
        if (feature) this.feature = feature;

        this._setOverlay();
        if (prevOverlay.getTooltip()) {
            this.bindTooltip(prevOverlay.getTooltip().getContent());
        }

        if (this.highlighted) this.setStyle(this.styles.highlight);

        if (this.map) {
            this.addToMap(this.map);
            this.map.removeLayer(prevOverlay);
        }
    },

    setOpacity(opacity) {
        // ENH: simplify this
        let finalFillOpacity;
        this._overlay.eachLayer(layer => {
            this._initialLayerOpacity = this._initialLayerOpacity || layer.options.opacity; // initial layer opacity from config page
            if (typeof layer.setStyle === 'function') {
                if (!layer.options.initialFillOpacity) {
                    layer.options.initialFillOpacity = layer.options.fillOpacity;
                }
                finalFillOpacity = layer.options.initialFillOpacity * opacity;
                layer.setStyle({
                    fillOpacity: finalFillOpacity,
                    opacity: this._initialLayerOpacity * opacity
                });
            }
            if (typeof layer.setOpacity === 'function') {
                layer.setOpacity(opacity);
            }
        });
    },

    /**
     * Sets the style on self
     * @param {L.Icon|Object} style Icon for point geometries or path options for other types (see {@link http://leafletjs.com/reference.html#path-options})
     */
    setStyle(style) {
        const gtype = this.getGeometryType();

        if (gtype == 'Point' || gtype == 'MultiPoint') {
            this._overlay.eachLayer(layer => {
                layer.setIcon(style.icon);
            });
        } else {
            this._overlay.eachLayer(layer => {
                layer.setStyle(style);
            });
        }
    },

    /**
     * @return {L.Bounds} the bounds of self
     */
    getBounds() {
        if (!this._bounds) {
            const gtype = this.getGeometryType();
            if (gtype == 'Point') {
                const coords = this.getGeometry().coordinates;
                this._bounds = new L.LatLngBounds([L.latLng(coords[1], coords[0])]);
            } else {
                this._bounds = this._overlay.getBounds();
            }
        }

        return this._bounds;
    },

    /**
     * Returns the centre point of self.
     * @return {L.LatLng}
     */
    getCenter() {
        if (this._overlay.getLatLng) {
            return this._overlay.getLatLng();
        } else {
            return this._overlay.getBounds().getCenter();
        }
    },

    enableDragging() {
        if (this._overlay.dragging) {
            this._overlay.dragging.enable();
        }
    },

    disableDragging() {
        if (this._overlay.dragging) {
            this._overlay.dragging.disable();
        }
    },

    /**
     * Binds a tooltip to self
     * Tooltip is shown on mouse over
     * @param  {String|HTMLElement|Function|Tooltip} labelText See http://leafletjs.com/reference-1.2.0.html#layer-bindtooltip
     * @param  {object} options   See http://leafletjs.com/reference-1.2.0.html#tooltip-option
     */
    bindTooltip(labelText, options) {
        options = _.extend({ sticky: true }, options);

        this._overlay.bindTooltip(labelText, options);
    },

    /**
     * Creates an instance of L.ILayer that represents this feature on a leaflet map.
     * This can be a L.FeatureGroup in the case where there are MultiPoints or a text style has been specified.
     * @return {L.ILayer}
     * @private
     */
    _setOverlay() {
        const geom = this.getGeometry();

        if (!geom) {
            console.log(
                `***Warning: ${this.feature.getUrn()} does not have geometry in ${this.worldName}`
            );
            return;
        }

        const coords = geom.coordinates;
        const overlay = new L.FeatureGroup([]);
        let geomoverlay;

        // Set the options here for geometryToLayer
        let geometryToLayerOptions = {
            bubblingMouseEvents: true
        };
        if (this.options.paneName) {
            geometryToLayerOptions.pane = this.options.paneName;
        }

        switch (geom.type) {
            case 'Point':
                this._addText(overlay, new L.LatLng(coords[1], coords[0]), 0);
                this._addSymbol(overlay, new L.LatLng(coords[1], coords[0]));

                break;

            case 'MultiPoint':
                _.each(coords, lngLat => {
                    this._addText(overlay, new L.LatLng(lngLat[1], lngLat[0]), 0);
                    this._addSymbol(overlay, new L.LatLng(lngLat[1], lngLat[0]));
                });

                break;

            case 'LineString':
                geomoverlay = this._addLine(overlay, geometryToLayerOptions);

                this._addTextToLine(overlay, geomoverlay);

                if (this.styles.normal.start_arrow == 'arrow')
                    this._addArrowToLine(overlay, geomoverlay, true);
                if (this.styles.normal.end_arrow == 'arrow')
                    this._addArrowToLine(overlay, geomoverlay, false);

                break;

            case 'MultiLineString':
                geomoverlay = L.GeoJSON.geometryToLayer(this.getGeometry(), geometryToLayerOptions);
                geomoverlay.setStyle(this.styles.normal);
                overlay.addLayer(geomoverlay);
                geomoverlay.myw_parent = overlay;

                _.each(geomoverlay.getLatLngs(), line => {
                    this._addTextToLine(overlay, line);
                });

                break;

            case 'Polygon':
            case 'MultiPolygon':
                geomoverlay = L.GeoJSON.geometryToLayer(this.getGeometry(), geometryToLayerOptions);
                geomoverlay.setStyle(this.styles.normal);
                overlay.addLayer(geomoverlay);
                geomoverlay.myw_parent = overlay;
                this._addText(overlay, geomoverlay.getBounds().getCenter(), 0);
        }

        this._bounds = null;
        this._overlay = overlay;
        this._editOverlay = geomoverlay;
    },

    /** Add text marker to overlay at specified position and orientation
     * @private
     */
    _addText(overlay, latLng, angle) {
        if (!this.styles.text) return;
        const propName = this.styles.text.fieldName;
        let text;
        if (propName.slice(-2) == '()') {
            try {
                text = this.feature[propName.slice(0, -2)]();
            } catch (e) {
                text = propName;
            }
        } else {
            text = this.feature.properties[propName];
        }
        if (!text) return;

        // Coerce value to text
        text = '' + text;

        // Simple support for multi-line text.
        text = text.replace(/\n/g, '<br>');

        this.styles.text.angle = angle;
        this.styles.text.text = text;

        if (this.options.textPaneName) {
            this.styles.text.pane = this.options.textPaneName;
        }

        const textMarker = new myw.TextMarker(latLng, this.styles.text);

        overlay.addLayer(textMarker);
    },

    /** Add text to overlay at midpoint of line
     * @private
     */
    _addTextToLine(overlay, line) {
        if (line instanceof L.Polyline) line = line.getLatLngs();

        // ENH Use of myw.app.map - problem in internals view?
        const midpoint = L.GeometryUtil.interpolateOnLine(myw.app.map, line, 0.5);

        const ptA = line[midpoint.predecessor];
        const ptB = line[midpoint.predecessor + 1];

        // Convert from north orientated bearing to X+ orientated bearing
        const angle = ptA.bearingTo(ptB) + 270;

        this._addText(overlay, midpoint.latLng, angle);
    },

    /** Create myWorld symbol
     * @private
     */
    _createSymbol(latlng) {
        const symbolStyle = this.styles.normal.symbol;
        const color = symbolStyle.color || 'transparent';
        const borderColor = symbolStyle.borderColor || 'transparent';
        const size = symbolStyle.size;
        const opacity = symbolStyle.opacity;
        const options = {
            fill: true,
            fillColor: color,
            weight: 2,
            stroke: true,
            color: borderColor,
            fillOpacity: opacity || 1.0,
            opacity: 1
        };
        const rotation = this.feature.properties[`myw_orientation_${this.geometryFieldName}`] || 0;
        let points, bnds, latlngs;

        if (symbolStyle.name != 'circle') {
            points = myw.svgRenderer.rotate(symbolStyle.name, rotation);
        }

        if (size[size.length - 1] == 'm') {
            //size in world meters
            const sizeInMeters = parseFloat(size.substring(0, size.length - 1));
            if (!points) {
                return new myw.Circle(latlng, _.extend(options, { radius: sizeInMeters / 2 }));
            } else {
                bnds = latlng.toBounds(sizeInMeters);

                const width = bnds.getEast() - bnds.getWest();
                const height = bnds.getNorth() - bnds.getSouth();
                latlngs = _.map(points, pt => ({
                    lat: height * ((pt[1] - 50) / 100) + latlng.lat,
                    lng: width * ((pt[0] - 50) / 100) + latlng.lng
                }));
                return new L.Polygon(latlngs, options);
            }
        } else {
            //size in pixels
            if (!points) {
                //circle
                options['radius'] = parseFloat(size) / 2;
                return new L.CircleMarker(latlng, options);
            } else {
                //not circle - drawn as polygon using a PolygonMarker to have a fixed pixel size
                if (size / 10 > 2) {
                    options.weight = size / 15;
                }
                const pixelPoints = _.map(points, pt => [
                    ((pt[0] - 50) * size) / 100,
                    ((100 - pt[1] - 50) * size) / 100
                ]);
                return new myw.PolygonMarker(latlng, pixelPoints, options);
            }
        }
    },

    /** Add symbol if required to overlay at specified position
     * @private
     */
    _addSymbol(overlay, latLng) {
        if (!this.styles.normal) return;

        if (this.styles.normal.symbol) {
            overlay.addLayer(this._createSymbol(latLng));
        } else {
            overlay.addLayer(new L.Marker(latLng, { icon: this.styles.normal.icon }));
        }
    },

    /* Add an arrow to overlay at specified position and angle
     * @private
     */
    _addArrow(overlay, pt1, angle) {
        // Angle one 'barb' of the arrow makes with the main line
        const theta = (Math.atan(this.arrowWidth / (this.arrowHeight * 2)) * 180) / Math.PI;

        let latLen = 0.0002;

        // Value of latlen for pixel weight is irrelevant at this stage as _setArrowSize will
        // calculate correct value.
        if (this.styles.normal.weightUnit == 'm') {
            latLen = this.arrowHeight * (this.styles.normal.weight / 40075017) * 360;
        }

        const pt = new L.LatLng(pt1.lat - latLen, pt1.lng),
            pt0 = L.GeometryUtil.rotatePoint(myw.app.map, pt, -theta + angle + 180, pt1),
            pt2 = L.GeometryUtil.rotatePoint(myw.app.map, pt, +theta + angle + 180, pt1),
            arrow = new L.Polygon([pt0, pt1, pt2]);

        /* For real-world units we want the line width to change as the zoom level does, for screen units, 
           we want the positioning of the arrow lines to change */
        if (this.styles.normal.weightUnit == 'm') {
            this._setWorldWeight(arrow);
        } else {
            //ENH: create a PixelArrowHead class and use it instead
            this._setArrowSize(arrow, angle);
        }

        const style = { fillColor: this.styles.normal.color, fillOpacity: 1.0, lineJoin: 'miter' };
        _.extend(style, this.styles.normal);
        delete style['dashArray'];
        arrow.isArrow = true;
        arrow.setStyle(style);
        overlay.addLayer(arrow);
    },

    /* Rotate a point around the origin
     * @private
     */
    _rotatePoint(pPoint, angleDeg) {
        const angleRad = (angleDeg * Math.PI) / 180,
            x = Math.cos(angleRad) * pPoint.x - Math.sin(angleRad) * pPoint.y,
            y = Math.sin(angleRad) * pPoint.x + Math.cos(angleRad) * pPoint.y;
        return new L.Point(x, y);
    },

    /** This changes the size of the arrow as the zoom level changes for arrows defined in pixel sizes
     * @private
     */
    _setArrowSize(geomoverlay, angle) {
        //ENH: create a PixelArrowHead class and use it instead
        const pt = new L.Point(0, this.arrowHeight * this.styles.normal.weight);
        const theta = (Math.atan(this.arrowWidth / (this.arrowHeight * 2)) * 180) / Math.PI;
        const pt0 = this._rotatePoint(pt, -theta + angle + 180);
        const pt2 = this._rotatePoint(pt, +theta + angle + 180);

        geomoverlay._projectLatlngs = function(latlngs, result, projectedBounds) {
            this._holePoints = [];

            // Only project the centre point
            const p1 = this._map.latLngToLayerPoint(latlngs[0][1]),
                p0 = new L.Point(p1.x + pt0.x, p1.y + pt0.y),
                p2 = new L.Point(p1.x + pt2.x, p1.y + pt2.y);
            projectedBounds.extend(p0);
            projectedBounds.extend(p1);
            projectedBounds.extend(p2);
            result.push([p0, p1, p2]);
        };
    },

    /* Add an arrow to overlay at either the start or end of line string
     * @private
     */
    _addArrowToLine(overlay, line, first) {
        if (line instanceof L.Polyline) line = line.getLatLngs();

        let ptA;
        let ptB;
        const len = line.length;

        if (first) {
            ptA = line[0];
            ptB = line[1];
        } else {
            ptA = line[len - 1];
            ptB = line[len - 2];
        }

        const angle = ptA.bearingTo(ptB);
        this._addArrow(overlay, ptA, angle);
    },

    /** This changes the width of the line as the zoom level changes
     * @private
     */
    _setWorldWeight(geomoverlay) {
        const originalWeight = this.styles.normal.weight,
            originalDash = this.styles.normal.dashArray;

        const originalFunction = geomoverlay._updatePath;
        geomoverlay._updatePath = function() {
            originalFunction.call(this);

            const z = this._map.getZoom();

            // Reset width to scale with zoom. Calibrated so that weight of 1 equates to roughtly 1 metre
            this.options.weight = originalWeight * Math.pow(2, z - 17);

            // Scale dash array
            if (L.Browser.svg && !geomoverlay.isArrow) {
                this.options.dashArray = _.map(originalDash, d => d * Math.pow(2, z - 17));
            }

            this.setStyle(this.options);
        };
    },

    /** Add the line representing this feature to the overlay
     * @private
     */
    _addLine(overlay, geometryToLayerOptions) {
        const geomoverlay = L.GeoJSON.geometryToLayer(this.getGeometry(), geometryToLayerOptions);

        if (this.styles.normal.weightUnit == 'm') {
            this._setWorldWeight(geomoverlay);
        }

        geomoverlay.setStyle(this.styles.normal);
        overlay.addLayer(geomoverlay);
        geomoverlay.myw_parent = overlay;

        return geomoverlay;
    },

    /**
     * Provides geometry as an overlay that can be edited.
     * @return {L.Ilayer}
     */
    getEditOverlay() {
        return this._editOverlay;
    },

    /**
     * @return {string}  geojson geometry type of this feature
     */
    getGeometryType() {
        const geom = this.getGeometry();
        return geom && geom.type;
    },

    /*
     * Returns whether or not this object should be sent to the back of the map after adding it
     */
    _shouldSendToBack() {
        const geomType = this.getGeometryType();
        return geomType == 'Polygon' || geomType == 'MultiPolygon';
    },

    _setupEventHandlers(eventHandlers) {
        if (eventHandlers === null) return;

        _.each(eventHandlers, eventHandler => {
            const eventName = eventHandler.eventName;
            const eventCallback = eventHandler.callback;

            //eventCallback should be of the form:
            //function (feature, featureRepresentation)  {//do something with feature and/or featureRepresentation}
            // the bind call will make the parameter association
            this._overlay.on(eventName, eventCallback.bind(null, this.feature, this._overlay));
        });
    },

    _getDefaultStyles() {
        const geomType = this.getGeometryType();

        if (!this._defaultStyles) {
            const markerHighlightStyle = myw.Util.fixIconDefPath(
                config['core.defaultMarkerStyleHighlight']
            );
            const markerNormalStyle = myw.Util.fixIconDefPath(
                config['core.defaultMarkerStyleNormal']
            );

            const styles = {
                Point: {
                    highlight: { icon: new L.Icon(markerHighlightStyle) },
                    normal: { icon: new L.Icon(markerNormalStyle) }
                },
                LineString: {
                    highlight: config['core.defaultPolylineStyleHighlight'],
                    normal: config['core.defaultPolylineStyleNormal']
                },
                Polygon: {
                    highlight: config['core.defaultPolygonStyleHighlight'],
                    normal: config['core.defaultPolygonStyleNormal']
                }
            };
            styles.MultiPoint = styles.Point;
            styles.MultiLineString = styles.LineString;
            styles.MultiPolygon = styles.Polygon;
            //store in prototype so it gets shared for future instances
            myw.FeatureRepresentation.prototype._defaultStyles = styles;
        }
        return this._defaultStyles[geomType];
    }
});

/**
 * A handler for an event
 * @typedef eventHandler
 * @property {string}      eventName   Name of the event to handle. Relevant ones out of {@link http://leafletjs.com/reference.html#map-events}
 * @property {Function}    callback    Function to execute when the event occurs.
 *                                     Will be provided with corresponding feature, featureRepresentation and event
 *                                     Should be of the form: function (feature, featureRepresentation, event) {}
 */

export default myw.FeatureRepresentation;
